import { PropsWithChildren, ReactNode } from 'react';

import { ButtonMantine, ButtonProps, CloseButton } from '../../../../Buttons';
import { Card, CardProps, ImageMantine } from '../../../../DataDisplay';
import { Skeleton } from '../../../../Feedback';
import { Group } from '../../../../Layout/Group';
import { Stack } from '../../../../Layout/Stack';
import { Box } from '../../../../Miscellaneous/Box';
import classes from './Announcements.module.css';

interface ButtonPropsWithOnClick extends ButtonProps {
  onClick?: () => void;
}

/**
 * Announcement component
 *
 * This component accepts both slots and props to render an announcement card.
 *
 * Slots are provided so that Pendo can render the content of the announcement.
 *
 * While props are provided to pass in the announcement data.
 *
 * Prefer using props over slots
 */

interface AnnouncementProps extends PropsWithChildren, CardProps {
  /** Accepts either a src or JSX.Element Image component */
  image?: JSX.Element | string;
  /** Accepts either a src or JSX.Element Image component */
  unread?: boolean;
  title?: string | ReactNode;
  onClose?: () => void;

  primaryButtonProps?: ButtonPropsWithOnClick;
  secondaryButtonProps?: ButtonPropsWithOnClick;

  isLoading?: boolean;
  /** Used for api announcement */
  buttons?: ReactNode[];
}

const AnnouncementImage = ({
  image,
  isLoading,
}: {
  isLoading?: boolean;
  image?: AnnouncementProps['image'];
}) => {
  if (!image) return null;

  return (
    <Skeleton width={50} visible={isLoading}>
      <Box className={classes.image}>
        {typeof image === 'string' ? <ImageMantine src={image} /> : image}
      </Box>
    </Skeleton>
  );
};

const AnnouncementButtons = ({
  primaryButtonProps,
  secondaryButtonProps,
  buttons,
}: Pick<AnnouncementProps, 'primaryButtonProps' | 'secondaryButtonProps' | 'buttons'>) => {
  if (!primaryButtonProps && !secondaryButtonProps && !buttons) return null;

  return (
    <Group justify="flex-end" gap="xs">
      {buttons}
      {secondaryButtonProps && (
        <ButtonMantine {...secondaryButtonProps} size="xs" variant="subtle">
          {secondaryButtonProps.children}
        </ButtonMantine>
      )}
      {primaryButtonProps && (
        <ButtonMantine {...primaryButtonProps} size="xs">
          {primaryButtonProps.children}
        </ButtonMantine>
      )}
    </Group>
  );
};

const Announcement = ({
  title,
  image = <ImageMantine />,
  children,

  unread = true,
  onClose,
  primaryButtonProps,
  secondaryButtonProps,
  buttons,
  isLoading = false,
  ...props
}: AnnouncementProps) => {
  return (
    <Card
      {...props}
      mod={{
        unread,
        image: image,
        ...props.mod,
      }}
      classNames={classes}
    >
      <Group align="flex-start" gap="sm" wrap="nowrap">
        <AnnouncementImage image={image} isLoading={isLoading} />

        <Stack gap="xs" w="100%">
          <Group justify="space-between" wrap="nowrap">
            <Skeleton visible={isLoading}>
              <Box className={classes.title}>{title}</Box>
            </Skeleton>
            {onClose && !isLoading && (
              <CloseButton
                variant="transparent"
                size="xs"
                iconSize={16}
                onClick={onClose}
                c="gray"
              />
            )}
          </Group>
          <Stack gap="xs">
            <Skeleton visible={isLoading}>
              <Box className={classes.description}>{children}</Box>
            </Skeleton>
            <AnnouncementButtons
              primaryButtonProps={primaryButtonProps}
              secondaryButtonProps={secondaryButtonProps}
              buttons={buttons}
            />
          </Stack>
        </Stack>
      </Group>
    </Card>
  );
};

interface _ButtonProps extends ButtonProps {
  primary?: boolean;
  secondary?: boolean;
}
const Button = ({ primary = true, secondary, ...props }: _ButtonProps) => {
  return <ButtonMantine size="xs" variant={primary ? 'filled' : 'subtle'} {...props} />;
};

Announcement.Button = Button;

export { Announcement };
